import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useReapplyOvertimeWithDetailsMutation } from "../../slices/overtimeApiSlice";
import { toast } from "sonner";

// Helper function to convert any date format to ISO format (YYYY-MM-DD)
const convertToISODate = (dateString) => {
  if (!dateString) return "";
  // If already in ISO format, return as-is
  if (dateString.includes("-") && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    return dateString;
  }
  // Otherwise convert via Date object
  try {
    const dateObj = new Date(dateString);
    return dateObj.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

export default function ReapplyOvertime() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedRecord = location.state?.selectedRecord;
  const previousHistoryData = location.state?.previousHistoryData; // Data from Re-Applied status

  // Debug: log the selected record to see all available fields
  // Removed for production

  const [reapplyOvertimeMutation, { isLoading: isReapplying }] =
    useReapplyOvertimeWithDetailsMutation();

  // History navigation state
  const historyRecords = selectedRecord?.history || [];
  const [historyIndex, setHistoryIndex] = useState(historyRecords.length - 1); // Start from most recent

  // Form states - ensure date is in ISO format (YYYY-MM-DD)
  const initialDate = convertToISODate(
    selectedRecord?.from?.split(" - ")[0] || "",
  );
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(
    selectedRecord?.from?.split(" - ")[1]?.split(" ")[0] || "",
  );
  const [endTime, setEndTime] = useState(
    selectedRecord?.to?.split(" - ")[1]?.split(" ")[0] || "",
  );
  const [hours, setHours] = useState(
    selectedRecord?.hours?.replace(" hrs", "") || "",
  );
  const [description, setDescription] = useState(
    selectedRecord?.description || "",
  );
  const [errors, setErrors] = useState({});

  // Calculate hours from time
  const calculateHours = (start, end) => {
    if (!start || !end) return "";
    try {
      const [startHour, startMin] = start.split(":").map(Number);
      const [endHour, endMin] = end.split(":").map(Number);
      const startTotalMin = startHour * 60 + startMin;
      const endTotalMin = endHour * 60 + endMin;
      const diffMin = endTotalMin - startTotalMin;
      const calculatedHours = (diffMin / 60).toFixed(1);
      return calculatedHours;
    } catch {
      return "";
    }
  };

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    const newHours = calculateHours(newStartTime, endTime);
    if (newHours) setHours(newHours);
  };

  const handleEndTimeChange = (e) => {
    const newEndTime = e.target.value;
    setEndTime(newEndTime);
    const newHours = calculateHours(startTime, newEndTime);
    if (newHours) setHours(newHours);
  };

  // Get current history record being displayed
  const currentHistoryRecord = historyRecords[historyIndex] || null;

  // Format date from ISO string
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Extract time from ISO datetime
  const extractTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const handlePreviousHistory = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
    }
  };

  const handleNextHistory = () => {
    if (historyIndex < historyRecords.length - 1) {
      setHistoryIndex(historyIndex + 1);
    }
  };

  // Get original hours from initial request (first history entry)
  const originalHours =
    historyRecords.length > 0
      ? parseFloat(historyRecords[0]?.totalHours || 0)
      : parseFloat(selectedRecord?.hours?.replace(" hrs", "") || 0);

  // Real-time validation
  React.useEffect(() => {
    const newErrors = {};

    // Validate date
    if (date) {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.date =
          "Cannot select future dates. Please select today or earlier.";
      }
    }

    // Validate start and end times
    if (startTime && endTime) {
      if (endTime <= startTime) {
        newErrors.time =
          "End time must be after start time. Please adjust the timing.";
      }
    }

    // Validate hours
    if (hours) {
      const hoursNum = parseFloat(hours);
      if (hoursNum <= 0) {
        newErrors.hours = "Hours must be greater than 0.";
      } else if (hoursNum > 24) {
        newErrors.hours = "Hours cannot exceed 24 in a single day.";
      } else if (hoursNum >= originalHours) {
        newErrors.hours = `You must reduce the hours. Original request was ${originalHours} hours, but you need to submit with fewer hours.`;
      }
    }

    // Validate description
    if (description.trim()) {
      if (description.trim().length < 5) {
        newErrors.description = "Description must be at least 5 characters.";
      } else if (description.trim().length > 500) {
        newErrors.description = "Description cannot exceed 500 characters.";
      }
    }

    setErrors(newErrors);
  }, [date, startTime, endTime, hours, description, originalHours]);

  const handleReapply = async () => {
    // Validation
    if (!date || !startTime || !endTime || !hours || !description.trim()) {
      toast.warning("Incomplete Information", {
        description: "Please fill in all required fields",
      });
      return;
    }

    // Check for validation errors
    if (Object.keys(errors).length > 0) {
      toast.error("Validation Error", {
        description: Object.values(errors).join(", "),
      });
      return;
    }

    // Check if data is identical to previous submission (if coming from Re-Applied status)
    if (previousHistoryData) {
      const prevStartTime = extractTime(previousHistoryData.startTime);
      const prevEndTime = extractTime(previousHistoryData.endTime);

      if (prevStartTime === startTime && prevEndTime === endTime) {
        toast.warning("No Changes Detected", {
          description:
            "The timing you entered is the same as the previous submission. Please modify the timing before reapplying.",
        });
        return;
      }
    }

    try {
      // Ensure date is in ISO format
      const isoDate = convertToISODate(date);

      if (!isoDate) {
        throw new Error("Invalid date format");
      }

      // Get the request ID - try different possible field names
      const requestId =
        selectedRecord?.id || selectedRecord?.overtimeId || selectedRecord?._id;

      if (!requestId) {
        throw new Error(
          "Request ID not found. Please go back and select a record again.",
        );
      }

      const [startHour, startMin] = startTime.split(":").map(String);
      const [endHour, endMin] = endTime.split(":").map(String);

      const startDateTime = `${isoDate}T${startHour}:${startMin}:00`;
      const endDateTime = `${isoDate}T${endHour}:${endMin}:00`;

      const payload = {
        requestId: requestId,
        startTime: startDateTime,
        endTime: endDateTime,
        totalHours: parseFloat(hours),
        description: description.trim(),
      };

      const result = await reapplyOvertimeMutation(payload).unwrap();

      // Success notification
      toast.success("Success!", {
        description: `Your overtime request has been resubmitted successfully. Request Details: Date: ${convertToISODate(
          date,
        )}, Time: ${startTime} - ${endTime}, Hours: ${hours} hrs.`,
      });

      navigate("/technician/overtime/records");
    } catch (error) {
      toast.error("Reapply Failed", {
        description:
          error?.data?.message ||
          "An error occurred while reapplying. Please try again.",
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft size={24} />
            </button>
            <h1 className="text-2xl font-semibold text-gray-900">
              Reapply overtime
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Reapply Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Reapply overtime requests
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Enter your overtime work details including date, start time and
              end time.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleReapply();
              }}
              className="space-y-6"
            >
              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    min={`${new Date().getFullYear()}-01-01`}
                    max={new Date().toISOString().split("T")[0]}
                    className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.date
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                  <input
                    type="text"
                    value={hours}
                    readOnly
                    placeholder="Hours"
                    className={`w-24 px-4 py-2 border rounded-lg bg-gray-50 text-gray-700 ${
                      errors.hours ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.date && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.date}
                  </p>
                )}
                {errors.hours && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.hours}
                  </p>
                )}
              </div>

              {/* Time Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={handleStartTimeChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.time
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={handleEndTimeChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.time
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                </div>
              </div>
              {errors.time && (
                <p className="text-red-500 text-sm flex items-center gap-1 -mt-4">
                  <span>⚠</span> {errors.time}
                </p>
              )}

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe overtime
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what overtime work was completed..."
                  rows={6}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 resize-none ${
                    errors.description
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <span>⚠</span> {errors.description}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setDate(initialDate);
                    setStartTime(
                      selectedRecord?.from?.split(" - ")[1]?.split(" ")[0] ||
                        "",
                    );
                    setEndTime(
                      selectedRecord?.to?.split(" - ")[1]?.split(" ")[0] || "",
                    );
                    setHours(selectedRecord?.hours?.replace(" hrs", "") || "");
                    setDescription(selectedRecord?.description || "");
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={isReapplying}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReapplying ? "Reapplying..." : "Reapply"}
                </button>
              </div>
            </form>
          </div>

          {/* Right Side - Previous Logs */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Previous Logs
            </h2>

            {selectedRecord ? (
              <div className="space-y-4">
                {/* User Profile */}
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-lg">
                      {selectedRecord.employee?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {selectedRecord.employee}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {selectedRecord.email}
                    </p>
                  </div>
                </div>

                {/* History Navigation Header */}
                {historyRecords.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-blue-900">
                        History Entry {historyIndex + 1} of{" "}
                        {historyRecords.length}
                      </span>
                      <span className="text-xs text-blue-700 px-2 py-1 bg-blue-100 rounded">
                        {currentHistoryRecord?.recordedBy === "INITIAL"
                          ? "Initial Request"
                          : "Reapply"}
                      </span>
                    </div>
                    {currentHistoryRecord?.recordedAt && (
                      <p className="text-xs text-blue-700 mb-3">
                        Recorded: {formatDate(currentHistoryRecord.recordedAt)}{" "}
                        at {extractTime(currentHistoryRecord.recordedAt)}
                      </p>
                    )}

                    {/* History Dropdown Selector */}
                    {historyRecords.length > 1 && (
                      <select
                        value={historyIndex}
                        onChange={(e) =>
                          setHistoryIndex(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-blue-300 rounded text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {historyRecords.map((record, idx) => (
                          <option key={idx} value={idx}>
                            {record.recordedBy === "INITIAL"
                              ? "Initial Request"
                              : "Reapply"}{" "}
                            - {formatDate(record.startTime)}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={handlePreviousHistory}
                        disabled={historyIndex === 0}
                        className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded text-sm font-medium hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={handleNextHistory}
                        disabled={historyIndex === historyRecords.length - 1}
                        className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded text-sm font-medium hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}

                {/* Title */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {currentHistoryRecord?.description ||
                      selectedRecord.description.split("\n")[0]}
                  </h3>
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm text-gray-600">
                    {currentHistoryRecord?.description ||
                      selectedRecord.description}
                  </p>
                </div>

                {/* Date and Time Info */}
                <div className="space-y-3 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      Date
                    </span>
                    <span className="text-sm text-gray-900">
                      {currentHistoryRecord
                        ? formatDate(currentHistoryRecord.startTime)
                        : convertToISODate(date)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      From:
                    </span>
                    <span className="text-sm text-gray-900">
                      {currentHistoryRecord
                        ? extractTime(currentHistoryRecord.startTime)
                        : startTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      To
                    </span>
                    <span className="text-sm text-gray-900">
                      {currentHistoryRecord
                        ? extractTime(currentHistoryRecord.endTime)
                        : endTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">
                      Hours
                    </span>
                    <span className="text-sm text-gray-900">
                      {currentHistoryRecord
                        ? currentHistoryRecord.totalHours
                        : hours}{" "}
                      hours
                    </span>
                  </div>
                </div>

                {/* Reapply Reason (if exists) */}
                {currentHistoryRecord?.reapplyReason && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        Supervisor's Reapply Request:
                      </p>
                      <p className="text-sm text-orange-900">
                        {currentHistoryRecord.reapplyReason}
                      </p>
                    </div>
                  </div>
                )}

                {/* Supervisor Note */}
                {selectedRecord.supervisorNote &&
                  selectedRecord.status !== "Re-Applied" &&
                  selectedRecord.status !== "REAPPLIED" && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-orange-700 font-semibold text-xs">
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-800 text-sm">
                            Supervisor
                          </h4>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                          {selectedRecord.supervisorEmail}
                        </p>
                        <p className="text-xs text-gray-500 font-medium mb-1">
                          Supervisor Request:
                        </p>
                        <p className="text-sm text-orange-900">
                          {selectedRecord.supervisorNote}
                        </p>
                      </div>
                    </div>
                  )}

                {/* History Navigation Buttons */}
                {historyRecords.length > 1 && (
                  <div className="pt-4 flex gap-2 justify-between">
                    <button
                      onClick={handlePreviousHistory}
                      disabled={historyIndex === 0}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={handleNextHistory}
                      disabled={historyIndex === historyRecords.length - 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600 text-center py-8">
                No record selected
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
